import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CellValueChangedEvent } from 'ag-grid-community';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetTemplate, useUpdateTemplate, useUploadTemplate, useTemplates } from '../../hooks/api/use-templates.hook';
import { TemplateApiModel } from '../../../../types/models/template.model';
import { LoadingIndicator } from '../LoadingIndicator';
import { TemplateForm } from './TemplateForm';
import TemplateSync from './TemplateSync';
import TemplateImport from './TemplateImport';
import { AutodeskLayerSyncPlanApiModel } from '../../../../types/models/sync/autodesk-layer-sync-plan.model';
import { Civil3dPropertySetSyncPlanApiModel } from '../../../../types/models/sync/civil3d-property-set-sync-plan.model';
import { Civil3dPropertySyncPlanApiModel } from '../../../../types/models/sync/civil3d-property-sync-plan.model';
import { LineStyleSyncPlanApiModel } from '../../../../types/models/sync/line-style-sync-plan.model';
import { PointLabelStyleSyncPlanApiModel } from '../../../../types/models/sync/point-label-style-sync-plan.model';
import { PointStyleSyncPlanApiModel } from '../../../../types/models/sync/point-style-sync-plan.model';
import { SyncPlan, SyncPlanStatus, SyncTemplatePlanStatus } from '../../../../types/constants/sync';
import { SyncPlanApiModel } from '../../../../types/models/sync/sync-plan.model';
import { useApplySyncPlan } from '../../hooks/api/use-sync.hook';

export const AddEditTemplate = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState<SyncPlanApiModel>({});
  const [templateErrors, setTemplateErrors] = useState<string[]>([]);
  const [applySyncPlan, { error: applySyncPlanError }] = useApplySyncPlan();

  const {
    template,
    loading: loadingTemplate,
    error: gettingError,
  } = useGetTemplate<TemplateApiModel & { files: File[] }>({ templateId: Number(templateId) });
  const [updateTemplate, { loading: updatingTemplate, error: updateError }] = useUpdateTemplate();
  const [uploadTemplate, { loading: uploadingTemplate, error: uploadError }] = useUploadTemplate();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<TemplateForm>({
    resolver: classValidatorResolver(TemplateForm, {
      transformer: { targetMaps: [{ target: TemplateForm, properties: { files: Array } }] },
    }),
    values: { name: template?.name ?? '', files: [] },
  });

  const { loading: loadingTemplates, response: templatesResponse } = useTemplates({});

  const options = templatesResponse
    ? templatesResponse?.pages
        .map(({ result }) => result)
        .flat()
        .map(({ id, name }) => ({ label: name, value: id }))
    : [];

  const supportedPlans = {
    pointLabelStyleSyncPlan: 'Point Label Style',
    pointStyleSyncPlan: 'Point Style',
    lineStyleSyncPlan: 'Line Style',
    autodeskLayerSyncPlan: 'Autodesk Layer',
    civil3dPropertySetSyncPlan: 'Civil 3D Property Set',
    civil3dPropertySyncPlan: 'Civil 3D Property',
  };

  const planTypeStatusMap = {
    create: SyncTemplatePlanStatus.NEW,
    update: SyncTemplatePlanStatus.REPLACE,
    delete: SyncTemplatePlanStatus.REMOVED,
  };

  const nameValue = watch('name');
  const filesValue = watch('files');

  const newTemplateOption = !template
    ? [
        {
          value: 'new',
          label: nameValue,
        },
      ]
    : [];

  const templateIdNameMap = [...options, ...newTemplateOption].reduce(
    (acc, { value, label }) => ({ ...acc, [value]: label }),
    {} as Record<string, string>,
  );

  const handleUploadTemplate = async (formData: FormData) => {
    const diffResponse = await uploadTemplate(formData);

    setPlans(diffResponse);
  };

  const handleCheckTemplates = () => {
    const currentTemplateErrors = Object.entries(plans).reduce((acc, [key, plan]) => {
      if (!Object.keys(supportedPlans).includes(key)) return acc;

      return [
        ...acc,
        ...Object.entries(plan).reduce((planAcc, [planType, value]) => {
          if (planType === SyncPlanStatus.INVALID || planType === SyncPlanStatus.DELETE || !Array.isArray(value))
            return planAcc;
          return [
            ...planAcc,
            ...(value as { name: string }[]).reduce((itemAcc: string[], item) => {
              if (('template' in item && item.template) || ('templates' in item && item.templates)) return itemAcc;
              return [
                ...itemAcc,
                `Template missing for "${item.name}" in ${planTypeStatusMap[planType as keyof typeof planTypeStatusMap].toLowerCase()} of the ${supportedPlans[key as keyof typeof supportedPlans].toLowerCase()} plan.`,
              ];
            }, [] as string[]),
          ];
        }, [] as string[]),
      ];
    }, [] as string[]);

    setTemplateErrors(currentTemplateErrors);

    return currentTemplateErrors;
  };

  const handleApplyDiff = async () => {
    const currentTemplateErrors = handleCheckTemplates();

    if (currentTemplateErrors.length > 0) {
      throw new Error('Cannot apply sync plan with template errors.');
    }

    await applySyncPlan(plans);
  };

  const handleConvertPlanToSync = <
    T extends { create: { name: string }[]; update: { name: string }[]; delete: { name: string }[] },
  >(
    plan: T,
  ): (T['create'][number] & { status: SyncTemplatePlanStatus })[] =>
    Object.entries(plan).flatMap(([key, value]) => {
      if (!Array.isArray(value)) {
        return [];
      }
      return value.map((item: { name: string; template?: { id: number }; templates?: { id: number }[] }) => ({
        ...item,
        status: planTypeStatusMap[key as keyof typeof planTypeStatusMap] as SyncTemplatePlanStatus,
        template: item.template?.id,
        templates: item.templates?.map(({ id }) => id),
      }));
    });

  const onSubmit: SubmitHandler<TemplateForm> = async (values) => {
    const formData = new FormData();

    formData.append('file', values.files[0]);
    formData.append('originalTemplateName', template?.name ?? values.name);
    const stepAction = step === 1 ? () => handleUploadTemplate(formData) : handleApplyDiff;

    await stepAction();
    setStep((prevStep) => prevStep + 1);

    if (step !== 2) return;
    if (template) {
      updateTemplate({ ...values, templateId: template.id });
    }
    navigate('..');
  };

  const renderStepOne = () => (
    <TemplateImport
      setError={setError}
      setValue={setValue}
      loading={uploadingTemplate || updatingTemplate}
      control={control}
      register={register}
      errors={errors}
      clearErrors={clearErrors}
      filesValue={filesValue}
    />
  );

  const handleFormatSelectedValue = (field: string, value: number[] | keyof typeof templateIdNameMap | string) => {
    if (field === 'templates') {
      return (value as number[]).map((id: number) => ({ id, name: templateIdNameMap[id] }));
    }
    if (field === 'template') {
      return { id: value, name: templateIdNameMap[value as keyof typeof templateIdNameMap] };
    }

    return value;
  };

  const handleRowChange = (plan: SyncPlan, { colDef, data, newValue: selectedValue }: CellValueChangedEvent) => {
    if (!colDef?.field) return;

    const fieldKey = data.name;

    const newValue = handleFormatSelectedValue(colDef.field as string, selectedValue);

    setPlans((prevPlans) => {
      const relatedPlan = prevPlans[plan as keyof SyncPlanApiModel];

      const statusPlanTypeMap = Object.entries(planTypeStatusMap).reduce(
        (acc, [key, value]) => ({ ...acc, [value]: key }),
        {} as Record<string, Omit<SyncPlanStatus, 'invalid'>>,
      );

      const planType = statusPlanTypeMap[data.status as keyof typeof statusPlanTypeMap];

      const items = relatedPlan ? relatedPlan[planType as keyof typeof relatedPlan] : null;

      if (!Array.isArray(items)) return prevPlans;

      const index = items.findIndex((item: { name?: string }) => item.name === fieldKey);

      return {
        ...prevPlans,
        [plan]: {
          ...relatedPlan,
          [planType as SyncPlanStatus]: [
            ...items.slice(0, index),
            { ...items[index], [colDef.field as string]: newValue },
            ...items.slice(index + 1),
          ],
        },
      };
    });
  };

  const renderStepTwo = () => (
    <TemplateSync
      layers={
        plans.autodeskLayerSyncPlan
          ? handleConvertPlanToSync<AutodeskLayerSyncPlanApiModel>(plans.autodeskLayerSyncPlan)
          : []
      }
      propertySets={
        plans.civil3dPropertySetSyncPlan
          ? handleConvertPlanToSync<Civil3dPropertySetSyncPlanApiModel>(plans.civil3dPropertySetSyncPlan)
          : []
      }
      userDefinedProperties={
        plans.civil3dPropertySyncPlan
          ? handleConvertPlanToSync<Civil3dPropertySyncPlanApiModel>(plans.civil3dPropertySyncPlan)
          : []
      }
      lineStyles={
        plans.lineStyleSyncPlan ? handleConvertPlanToSync<LineStyleSyncPlanApiModel>(plans.lineStyleSyncPlan) : []
      }
      pointLabelStyles={
        plans.pointLabelStyleSyncPlan
          ? handleConvertPlanToSync<PointLabelStyleSyncPlanApiModel>(plans.pointLabelStyleSyncPlan)
          : []
      }
      pointStyles={
        plans.pointStyleSyncPlan ? handleConvertPlanToSync<PointStyleSyncPlanApiModel>(plans.pointStyleSyncPlan) : []
      }
      onRowChange={handleRowChange}
      templateIdNameMap={template ? templateIdNameMap : { ...templateIdNameMap, new: nameValue }}
      templateOptions={template ? options : [...options, { label: nameValue, value: 'new' }]}
      loading={loadingTemplates}
      errors={templateErrors}
    />
  );

  if (loadingTemplate) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  if (gettingError) {
    return <Alert severity="error">{gettingError?.response?.data.message}</Alert>;
  }

  return (
    <Dialog open maxWidth="xl" fullWidth>
      {loadingTemplate ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{step === 1 ? `${template ? 'Edit' : 'Add'} Template` : 'Template Sync'}</DialogTitle>
          <DialogContent>
            {step === 1 ? renderStepOne() : renderStepTwo()}
            {updateError || uploadError || applySyncPlanError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {(updateError || uploadError || applySyncPlanError)?.response?.data?.message ??
                  updateError?.response?.data?.message ??
                  uploadError?.response?.data?.message ??
                  applySyncPlanError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {step === 1 ? 'Import Template' : 'Apply'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
